/**
 * Auditlogs service
 */

import logger from 'core/utils/logger';
import { get, post, put } from 'api/axios-client';
import { API_ENDPOINTS } from '../constants';
import {
  ActivityLog,
  ActivityLogCategory,
  PaginatedResponse,
  ApiResponse,
} from '../types';

// Mock data for different pages
const MOCK_ACTIVITY_LOGS: ActivityLog[] = [
  // Recent AuditLogs (Dashboard)
  {
    id: '1',
    title: 'User Login',
    description: 'John Doe successfully logged into the system',
    timestamp: new Date().toISOString(),
    category: 'authentication',
    severity: 'info',
    userId: 'user1',
    status: 'completed',
    metadata: {
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
      location: 'New York, NY',
    },
  },
  {
    id: '2',
    title: 'Data Export',
    description: 'Customer data exported to CSV format',
    timestamp: new Date(Date.now() - 1800000).toISOString(), // 30 minutes ago
    category: 'data',
    severity: 'info',
    userId: 'user2',
    status: 'completed',
    metadata: {
      recordCount: 150,
      fileSize: '2.5MB',
      format: 'CSV',
    },
  },
  {
    id: '3',
    title: 'System Maintenance',
    description: 'Scheduled database backup completed successfully',
    timestamp: new Date(Date.now() - 3600000).toISOString(), // 1 hour ago
    category: 'system',
    severity: 'info',
    userId: 'system',
    status: 'completed',
    metadata: {
      backupSize: '15.2GB',
      duration: '45 minutes',
    },
  },
  {
    id: '4',
    title: 'Failed Login Attempt',
    description: 'Multiple failed login attempts detected',
    timestamp: new Date(Date.now() - 7200000).toISOString(), // 2 hours ago
    category: 'security',
    severity: 'warning',
    userId: 'unknown',
    status: 'failed',
    metadata: {
      ipAddress: '203.0.113.1',
      attemptCount: 5,
      blocked: true,
    },
  },
  {
    id: '5',
    title: 'Report Generated',
    description: 'Monthly sales report generated and sent',
    timestamp: new Date(Date.now() - 10800000).toISOString(), // 3 hours ago
    category: 'reporting',
    severity: 'info',
    userId: 'user3',
    status: 'completed',
    metadata: {
      reportType: 'Sales',
      period: '2024-01',
      recipients: 5,
    },
  },

  // All Logs (AuditLogs Page)
  {
    id: '6',
    title: 'User Profile Updated',
    description: 'User profile information was modified',
    timestamp: new Date(Date.now() - 14400000).toISOString(), // 4 hours ago
    category: 'user',
    severity: 'info',
    userId: 'user1',
    status: 'completed',
    metadata: {
      fieldsChanged: ['email', 'phone'],
      previousEmail: 'john.old@email.com',
    },
  },
  {
    id: '7',
    title: 'Database Query Error',
    description: 'Slow query detected in customer table',
    timestamp: new Date(Date.now() - 18000000).toISOString(), // 5 hours ago
    category: 'error',
    severity: 'error',
    userId: 'system',
    status: 'failed',
    metadata: {
      query: 'SELECT * FROM customers WHERE...',
      executionTime: '15.2s',
      table: 'customers',
    },
  },
  {
    id: '8',
    title: 'File Upload',
    description: 'Document uploaded to document management system',
    timestamp: new Date(Date.now() - 21600000).toISOString(), // 6 hours ago
    category: 'data',
    severity: 'info',
    userId: 'user4',
    status: 'completed',
    metadata: {
      fileName: 'contract_2024.pdf',
      fileSize: '1.2MB',
      fileType: 'PDF',
    },
  },
  {
    id: '9',
    title: 'Permission Changed',
    description: 'User permissions updated for admin role',
    timestamp: new Date(Date.now() - 25200000).toISOString(), // 7 hours ago
    category: 'security',
    severity: 'warning',
    userId: 'admin1',
    status: 'completed',
    metadata: {
      targetUser: 'user5',
      newRole: 'admin',
      previousRole: 'user',
    },
  },
  {
    id: '10',
    title: 'API Rate Limit Exceeded',
    description: 'API rate limit exceeded for external service',
    timestamp: new Date(Date.now() - 28800000).toISOString(), // 8 hours ago
    category: 'api',
    severity: 'warning',
    userId: 'system',
    status: 'failed',
    metadata: {
      service: 'payment-gateway',
      limit: 1000,
      current: 1001,
    },
  },

  // My Logs (User-specific)
  {
    id: '11',
    title: 'Password Changed',
    description: 'User password was successfully updated',
    timestamp: new Date(Date.now() - 32400000).toISOString(), // 9 hours ago
    category: 'authentication',
    severity: 'info',
    userId: 'user1',
    status: 'completed',
    metadata: {
      method: 'self-service',
      strength: 'strong',
    },
  },
  {
    id: '12',
    title: 'Settings Updated',
    description: 'User preferences and settings modified',
    timestamp: new Date(Date.now() - 36000000).toISOString(), // 10 hours ago
    category: 'user',
    severity: 'info',
    userId: 'user1',
    status: 'completed',
    metadata: {
      settingsChanged: ['theme', 'notifications', 'language'],
    },
  },
  {
    id: '13',
    title: 'Search Performed',
    description: 'Advanced search executed in customer database',
    timestamp: new Date(Date.now() - 39600000).toISOString(), // 11 hours ago
    category: 'search',
    severity: 'info',
    userId: 'user1',
    status: 'completed',
    metadata: {
      searchTerm: 'Acme Corporation',
      resultsCount: 25,
      filters: ['active', 'premium'],
    },
  },
  {
    id: '14',
    title: 'Logout',
    description: 'User logged out of the system',
    timestamp: new Date(Date.now() - 43200000).toISOString(), // 12 hours ago
    category: 'authentication',
    severity: 'info',
    userId: 'user1',
    status: 'completed',
    metadata: {
      sessionDuration: '4h 32m',
      lastActivity: '5 minutes ago',
    },
  },
  {
    id: '15',
    title: 'Data Validation Error',
    description: 'Invalid data format detected during import',
    timestamp: new Date(Date.now() - 46800000).toISOString(), // 13 hours ago
    category: 'error',
    severity: 'error',
    userId: 'user1',
    status: 'failed',
    metadata: {
      file: 'import_data.xlsx',
      row: 45,
      field: 'email',
      error: 'Invalid email format',
    },
  },
];

const MOCK_CATEGORIES: ActivityLogCategory[] = [
  {
    id: '1',
    name: 'Authentication',
    description: 'Login, logout, and security events',
    color: '#2196F3',
  },
  {
    id: '2',
    name: 'Data Operations',
    description: 'Data creation, modification, and deletion',
    color: '#4CAF50',
  },
  {
    id: '3',
    name: 'System Events',
    description: 'System-level events and maintenance',
    color: '#FF9800',
  },
  {
    id: '4',
    name: 'User Actions',
    description: 'User-initiated actions and interactions',
    color: '#9C27B0',
  },
  {
    id: '5',
    name: 'Error Events',
    description: 'Errors, exceptions, and failures',
    color: '#F44336',
  },
  {
    id: '6',
    name: 'Security',
    description: 'Security-related events and alerts',
    color: '#795548',
  },
  {
    id: '7',
    name: 'Reporting',
    description: 'Report generation and distribution',
    color: '#607D8B',
  },
  {
    id: '8',
    name: 'API',
    description: 'API calls and external service interactions',
    color: '#00BCD4',
  },
  {
    id: '9',
    name: 'Search',
    description: 'Search operations and queries',
    color: '#8BC34A',
  },
];

export interface ActivityLogsQuery {
  page?: number;
  limit?: number;
  categoryId?: string;
  search?: string;
  status?: string;
  type?: string;
  startDate?: string;
  endDate?: string;
}

class ActivityLogsService {
  async getActivityLogs(
    query: ActivityLogsQuery = {}
  ): Promise<PaginatedResponse<ActivityLog>> {
    try {
      logger.info('Fetching Auditlogs', { query });

      // Use mock data for now
      let filteredLogs = [...MOCK_ACTIVITY_LOGS];

      // Apply filters
      if (query.categoryId) {
        filteredLogs = filteredLogs.filter(
          log => log.category === query.categoryId
        );
      }

      if (query.search) {
        const searchTerm = query.search.toLowerCase();
        filteredLogs = filteredLogs.filter(
          log =>
            log.title.toLowerCase().includes(searchTerm) ||
            log.description.toLowerCase().includes(searchTerm)
        );
      }

      if (query.status) {
        filteredLogs = filteredLogs.filter(log => log.status === query.status);
      }

      if (query.type) {
        filteredLogs = filteredLogs.filter(log => log.severity === query.type);
      }

      if (query.startDate && query.endDate) {
        const startDate = new Date(query.startDate);
        const endDate = new Date(query.endDate);
        filteredLogs = filteredLogs.filter(log => {
          const logDate = new Date(log.timestamp);
          return logDate >= startDate && logDate <= endDate;
        });
      }

      // Sort by timestamp (newest first)
      filteredLogs.sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      // Pagination
      const page = query.page || 1;
      const limit = query.limit || 10;
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedLogs = filteredLogs.slice(startIndex, endIndex);

      const response: PaginatedResponse<ActivityLog> = {
        data: paginatedLogs,
        pagination: {
          page,
          limit,
          total: filteredLogs.length,
          totalPages: Math.ceil(filteredLogs.length / limit),
        },
      };

      await new Promise(resolve => setTimeout(resolve, 500));

      logger.info('Auditlogs fetched successfully', {
        count: paginatedLogs.length,
        total: filteredLogs.length,
      });
      return response;
    } catch (error) {
      logger.error('Failed to fetch Auditlogs', { query }, error as Error);
      throw error;
    }
  }

  async getRecentActivityLogs(limit: number = 5): Promise<ActivityLog[]> {
    const response = await this.getActivityLogs({ limit });
    return response.data;
  }

  async getMyActivityLogs(
    userId: string,
    limit?: number
  ): Promise<ActivityLog[]> {
    const response = await this.getActivityLogs({
      // Add userId filter when available in query interface
      limit,
    });
    // Filter by userId for now
    return response.data.filter(log => log.userId === userId);
  }

  async getActivityLog(id: string): Promise<ActivityLog> {
    try {
      logger.info('Fetching Auditlog', { id });

      const log = MOCK_ACTIVITY_LOGS.find(l => l.id === id);

      if (!log) {
        throw new Error(`Auditlog with ID ${id} not found`);
      }

      await new Promise(resolve => setTimeout(resolve, 300));

      logger.info('Auditlog fetched successfully', { id });
      return log;
    } catch (error) {
      logger.error('Failed to fetch Auditlog', { id }, error as Error);
      throw error;
    }
  }

  async createActivityLog(
    data: Omit<ActivityLog, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<ActivityLog> {
    try {
      logger.info('Creating Auditlog', { title: data.title });

      const response = await post<ActivityLog>(
        API_ENDPOINTS.ACTIVITY_LOGS.CREATE,
        data
      );

      if (response) {
        logger.info('Auditlog created successfully', {
          id: response.data.id,
        });
        return response.data;
      } else {
        throw new Error(response || 'Failed to create Auditlog');
      }
    } catch (error) {
      logger.error('Failed to create Auditlog', { data }, error as Error);
      throw error;
    }
  }

  async updateActivityLog(
    id: string,
    data: Partial<ActivityLog>
  ): Promise<ActivityLog> {
    try {
      logger.info('Updating Auditlog', { id });

      const response = await put<ActivityLog>(
        API_ENDPOINTS.ACTIVITY_LOGS.UPDATE.replace(':id', id),
        data
      );

      if (response) {
        logger.info('Auditlog updated successfully', { id });
        return response.data;
      } else {
        throw new Error(response || 'Failed to update Auditlog');
      }
    } catch (error) {
      logger.error('Failed to update Auditlog', { id, data }, error as Error);
      throw error;
    }
  }

  async getCategories(): Promise<ActivityLogCategory[]> {
    try {
      logger.info('Fetching Auditlog categories');

      await new Promise(resolve => setTimeout(resolve, 200));

      logger.info('Categories fetched successfully', {
        count: MOCK_CATEGORIES.length,
      });
      return MOCK_CATEGORIES;
    } catch (error) {
      logger.error('Failed to fetch categories', {}, error as Error);
      throw error;
    }
  }

  async getActivityLogStats(): Promise<{
    total: number;
    byCategory: Record<string, number>;
    bySeverity: Record<string, number>;
    byStatus: Record<string, number>;
  }> {
    try {
      logger.info('Fetching Auditlog statistics');

      const stats = {
        total: MOCK_ACTIVITY_LOGS.length,
        byCategory: {} as Record<string, number>,
        bySeverity: {} as Record<string, number>,
        byStatus: {} as Record<string, number>,
      };

      MOCK_ACTIVITY_LOGS.forEach(log => {
        // Count by category
        stats.byCategory[log.category] =
          (stats.byCategory[log.category] || 0) + 1;

        // Count by severity
        stats.bySeverity[log.severity] =
          (stats.bySeverity[log.severity] || 0) + 1;

        // Count by status
        const status = log.status || 'unknown';
        stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;
      });

      await new Promise(resolve => setTimeout(resolve, 300));

      logger.info('Auditlog statistics fetched successfully');
      return stats;
    } catch (error) {
      logger.error('Failed to fetch Auditlog statistics', {}, error as Error);
      throw error;
    }
  }
}

export const activityLogsService = new ActivityLogsService();
export default activityLogsService;
